import type { ReactNode } from 'react'
import { sx } from '../../../lib/sx'
import { Hov } from '../../../lib/Hov'
import type { Vals } from '../../../demo/vals'

export function GroupDashboard({ v }: { v: Vals }) {
  return (
    <div style={sx("position:absolute;inset:0;padding-top:56px;display:flex;flex-direction:column")}>
      <div style={sx("flex:1;overflow:auto;display:flex;flex-direction:column")}>
        <div style={sx("padding:8px 20px 22px")}>
          <button onClick={v.backFromPush} style={sx("width:36px;height:36px;border-radius:var(--radius-full);border:none;background:var(--bg-tertiary);cursor:pointer;display:flex;align-items:center;justify-content:center")}>
            <span style={sx("width:18px;height:18px;background:var(--text-primary);-webkit-mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat;mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat")} />
          </button>
        </div>
        <div style={sx("padding:0 20px 20px;display:flex;flex-direction:column;gap:12px")}>
          {v.hasGroup ? (
            <>
              <SubscriptionCard v={v} />

              <SectionLabel>Group management</SectionLabel>

              <GroupCard v={v} />

              <div style={sx("display:flex;flex-direction:column;gap:8px")}>
                <SectionLabel>Group members</SectionLabel>

                {v.members.map((m) => (
                  <MemberRow key={m.key} v={v} m={m} />
                ))}

                {v.canInviteMore ? (
                  <Hov
                    as="button"
                    onClick={v.openInvite}
                    style={sx(
                      "padding:14px 16px;border-radius:var(--radius-md);border:1px dashed var(--border-primary);" +
                        "background:transparent;color:var(--text-primary);font-family:inherit;cursor:pointer;" +
                        "display:flex;align-items:center;gap:12px;transition:all 150ms var(--ease-out)"
                    )}
                    hoverStyle={sx("background:var(--bg-secondary)")}
                  >
                    <span
                      style={sx(
                        "width:40px;height:40px;flex:none;border-radius:var(--radius-full);" +
                          "background:var(--bg-tertiary);display:flex;align-items:center;justify-content:center"
                      )}
                    >
                      <span
                        style={sx(
                          "width:20px;height:20px;background:var(--text-accent);" +
                            "-webkit-mask:url(/assets/icons/plus.svg) center/contain no-repeat;" +
                            "mask:url(/assets/icons/plus.svg) center/contain no-repeat"
                        )}
                      />
                    </span>
                    <span style={sx("font-size:var(--fs-sm);font-weight:var(--fw-medium)")}>
                      Invite members
                    </span>
                  </Hov>
                ) : null}
              </div>

              {v.showGroupStats ? (
                <>
                  <SectionLabel>Group features</SectionLabel>
                  <GroupStatsCard v={v} />
                </>
              ) : null}

              {/* Zero knowledge only. The same two cards appear on the member's
                  profile — in a device-to-device model neither side is the
                  authority, so neither gets a different screen. */}
              {v.showGroupAlertsCard ? (
                <>
                  <SectionLabel>Group features</SectionLabel>
                  {/* Pairs that already exist come first: they are the state of
                      the group, and the two handshake entry points below are
                      only how you add to it. */}
                  {v.pairedDevices.map((d) => (
                    <PairedDeviceRow key={d.key} v={v} d={d} />
                  ))}
                  <PairingCard
                    card={v.shareCards[0]}
                    onOpen={v.managerOpenShareInvite}
                  />
                  <PairingCard card={v.shareCards[1]} onOpen={v.managerOpenScanner} />
                </>
              ) : null}
            </>
          ) : null}
          {v.noGroupYet ? (
            <>
              <SubscriptionCard v={v} />

              <div style={sx("padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:14px")}>
                <span style={sx("font-size:var(--fs-md);font-weight:var(--fw-medium)")}>
                  Protect your close ones with NordVPN Group account
                </span>
                <span style={sx("display:flex;gap:12px;align-items:flex-start")}>
                  <span style={sx("width:20px;height:20px;flex:none;margin-top:2px;background:var(--text-accent);-webkit-mask:url(/assets/icons/lock.svg) center/contain no-repeat;mask:url(/assets/icons/lock.svg) center/contain no-repeat")} />
                  <span style={sx("display:flex;flex-direction:column;gap:2px")}>
                    <span style={sx("font-size:var(--fs-sm);font-weight:var(--fw-medium)")}>
                      5 separate accounts
                    </span>
                    <span style={sx("font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)")}>
                      Everyone signs in as themselves. You never see their activity.
                    </span>
                  </span>
                </span>
                <span style={sx("display:flex;gap:12px;align-items:flex-start")}>
                  <span style={sx("width:20px;height:20px;flex:none;margin-top:2px;background:var(--text-accent);-webkit-mask:url(/assets/icons/threat-protection.svg) center/contain no-repeat;mask:url(/assets/icons/threat-protection.svg) center/contain no-repeat")} />
                  <span style={sx("display:flex;flex-direction:column;gap:2px")}>
                    <span style={sx("font-size:var(--fs-sm);font-weight:var(--fw-medium)")}>
                      Everyone gets Complete
                    </span>
                    <span style={sx("font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)")}>
                      Same protection you have, on their own 10 devices, paid by you.
                    </span>
                  </span>
                </span>
                <span style={sx("display:flex;gap:12px;align-items:flex-start")}>
                  <span style={sx("width:20px;height:20px;flex:none;margin-top:2px;background:var(--text-accent);-webkit-mask:url(/assets/icons/bell.svg) center/contain no-repeat;mask:url(/assets/icons/bell.svg) center/contain no-repeat")} />
                  <span style={sx("display:flex;flex-direction:column;gap:2px")}>
                    <span style={sx("font-size:var(--fs-sm);font-weight:var(--fw-medium)")}>
                      Alerts you can act on
                    </span>
                    <span style={sx("font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)")}>
                      See threats blocked on the devices you look after — if they allow it.
                    </span>
                  </span>
                </span>
                <span
                  style={sx(
                    "font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary);" +
                      "padding-top:14px;border-top:1px solid var(--border-secondary)"
                  )}
                >
                  Group members get their own Complete plan. Your own plan and its 28 Aug renewal stay exactly as they are.
                </span>
              <Hov as="button" onClick={v.openTerm} style={sx("height:52px;flex:none;border-radius:var(--radius-full);border:none;background:var(--bg-accent);color:#fff;font-family:inherit;font-size:var(--fs-md);font-weight:var(--fw-medium);cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px")} hoverStyle={sx("background:var(--bg-accent-active)")}>
                <span style={sx("width:16px;height:16px;background:#fff;-webkit-mask:url(/assets/icons/plus.svg) center/contain no-repeat;mask:url(/assets/icons/plus.svg) center/contain no-repeat")} />
                <span style={sx("font-weight:var(--fw-medium)")}>
                  {"Add members "}
                  <span style={sx("font-weight:var(--fw-normal);opacity:.85")}>
                    {"(starting "}
                    {v.groupFromEurSuffix}
                    /mo)
                  </span>
                </span>
              </Hov>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Plan, renewal and term. Identical whether or not a group exists yet. */
function SubscriptionCard({ v }: { v: Vals }) {
  return (
    <div
      style={sx(
        'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:2px'
      )}
    >
      <span
        style={sx(
          'font-size:var(--fs-2xs);font-weight:var(--fw-medium);letter-spacing:.06em;' +
            'text-transform:uppercase;color:var(--text-secondary)'
        )}
      >
        Current subscription
      </span>
      <span style={sx('font-size:var(--fs-xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)')}>
        {v.subPlanName}
      </span>
      <span style={sx('font-size:var(--fs-xs);color:var(--text-secondary)')}>{v.subPlanLine}</span>
    </div>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={sx(
        'font-size:var(--fs-xs);font-weight:var(--fw-medium);color:var(--text-secondary);' +
          'letter-spacing:.04em;text-transform:uppercase;margin-top:4px'
      )}
    >
      {children}
    </span>
  )
}

/**
 * Group identity and capacity in one card: the name, renamed in place, over the
 * seat meter. The name is the heading here, so it carries no label of its own —
 * tapping it starts editing, and the icon is there to say that it can be.
 */
function GroupCard({ v }: { v: Vals }) {
  return (
    <div
      style={sx(
        'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:14px'
      )}
    >
      {v.isEditingGroupName ? (
        <div style={sx('display:flex;align-items:center;gap:10px')}>
          <input
            autoFocus
            value={v.groupNameDraft}
            onChange={(e) => v.changeGroupNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') v.saveGroupName()
              if (e.key === 'Escape') v.cancelEditGroupName()
            }}
            style={sx(
              'flex:1;min-width:0;height:38px;padding:0 12px;border-radius:var(--radius-sm);' +
                'border:1px solid var(--border-accent);background:var(--bg-primary);' +
                'color:var(--text-primary);font-family:inherit;font-size:var(--fs-lg);' +
                'font-weight:var(--fw-bold);outline:none'
            )}
          />
          <IconButton icon="checkmark" label="Save group name" onClick={v.saveGroupName} accent />
          <IconButton icon="close" label="Cancel" onClick={v.cancelEditGroupName} />
        </div>
      ) : (
        <div style={sx('display:flex;align-items:center;gap:12px')}>
          <Hov
            as="button"
            onClick={v.startEditGroupName}
            title="Rename group"
            style={sx(
              'flex:1;min-width:0;padding:0;border:none;background:transparent;text-align:left;' +
                'font-family:inherit;color:var(--text-primary);cursor:pointer;' +
                'font-size:var(--fs-xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)'
            )}
            hoverStyle={sx('opacity:.7')}
          >
            {v.groupName}
          </Hov>
          <IconButton icon="edit" label="Rename group" onClick={v.startEditGroupName} />
        </div>
      )}

      <div
        style={sx(
          'display:flex;flex-direction:column;gap:12px;' +
            'padding-top:14px;border-top:1px solid var(--border-secondary)'
        )}
      >
        <div style={sx('display:flex;justify-content:space-between;align-items:baseline;gap:12px')}>
          <span style={sx('font-size:var(--fs-md);font-weight:var(--fw-medium)')}>
            {v.seatsUsed}
            {' of '}
            {v.seatsTotal}
            {' seats used'}
          </span>
          <span style={sx('font-size:var(--fs-xs);color:var(--text-secondary);flex:none')}>
            {v.seatsLeftLabel}
          </span>
        </div>
        <div style={sx('display:flex;gap:6px')}>
          {v.seatBars.map((bar, i) => (
            <div
              key={i}
              style={sx(`flex:1;height:6px;border-radius:var(--radius-full);background:${bar.color}`)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

type Member = Vals['members'][number]

/**
 * One person in the group.
 *
 * The chevron only appears under the mapped model, where a member has devices
 * behind them. Renaming lives on the row itself so it works either way.
 */
function MemberRow({ v, m }: { v: Vals; m: Member }) {
  const editing = v.aliasEditingKey === m.key
  const isOwner = m.key === 'self'
  const hasDepth = v.membersHaveDepth && !isOwner

  if (editing) {
    return (
      <div
        style={sx(
          'padding:14px 16px;border-radius:var(--radius-md);background:var(--bg-secondary);' +
            'border:1px solid var(--border-accent);display:flex;align-items:center;gap:10px'
        )}
      >
        <input
          autoFocus
          placeholder={m.email}
          value={v.aliasDraft}
          onChange={(e) => v.changeAliasDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') v.saveAlias()
            if (e.key === 'Escape') v.cancelAlias()
          }}
          style={sx(
            'flex:1;min-width:0;height:36px;padding:0 12px;border-radius:var(--radius-sm);' +
              'border:1px solid var(--border-input);background:var(--bg-primary);' +
              'color:var(--text-primary);font-family:inherit;font-size:var(--fs-sm);outline:none'
          )}
        />
        <IconButton icon="checkmark" label="Save alias" onClick={v.saveAlias} accent />
        <IconButton icon="close" label="Cancel" onClick={v.cancelAlias} />
      </div>
    )
  }

  return (
    <div
      style={sx(
        'padding:14px 16px;border-radius:var(--radius-md);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);display:flex;align-items:center;gap:12px'
      )}
    >
      <span
        style={sx(
          `width:40px;height:40px;flex:none;border-radius:var(--radius-full);background:${m.tint};` +
            `color:#fff;display:flex;align-items:center;justify-content:center;` +
            `font-size:var(--fs-sm);font-weight:var(--fw-medium)`
        )}
      >
        {m.initials}
      </span>

      <Hov
        as={hasDepth ? 'button' : 'span'}
        onClick={hasDepth ? m.open : undefined}
        style={sx(
          'flex:1;min-width:0;padding:0;border:none;background:transparent;text-align:left;' +
            'font-family:inherit;color:var(--text-primary);display:flex;flex-direction:column;gap:2px' +
            (hasDepth ? ';cursor:pointer' : '')
        )}
        hoverStyle={hasDepth ? sx('opacity:.75') : undefined}
      >
        <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>{m.name}</span>
        <span style={sx('font-size:var(--fs-xs);color:var(--text-secondary)')}>{m.role}</span>
      </Hov>

      <span
        style={sx(
          `height:24px;padding:0 10px;border-radius:var(--radius-full);background:${m.statusBg};` +
            `color:${m.statusColor};font-size:var(--fs-2xs);font-weight:var(--fw-medium);` +
            `display:flex;align-items:center;flex:none`
        )}
      >
        {m.statusLabel}
      </span>

      {isOwner ? null : (
        <IconButton
          icon="edit"
          label={`Rename ${m.name}`}
          onClick={() => v.startAlias(m.key, m.email)}
        />
      )}

      {hasDepth ? (
        <span
          style={sx(
            'width:16px;height:16px;flex:none;opacity:.5;background:var(--text-primary);' +
              '-webkit-mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat;' +
              'mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat'
          )}
        />
      ) : null}
    </div>
  )
}

function IconButton({
  icon,
  label,
  onClick,
  accent,
}: {
  icon: string
  label: string
  onClick: () => void
  accent?: boolean
}) {
  return (
    <Hov
      as="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={sx(
        'width:32px;height:32px;flex:none;padding:0;border-radius:var(--radius-full);border:none;' +
          `background:${accent ? 'var(--bg-accent)' : 'var(--bg-tertiary)'};cursor:pointer;` +
          'display:flex;align-items:center;justify-content:center'
      )}
      hoverStyle={sx(accent ? 'background:var(--bg-accent-active)' : 'background:var(--border-primary)')}
    >
      <span
        style={sx(
          `width:15px;height:15px;background:${accent ? '#fff' : 'var(--text-primary)'};` +
            `-webkit-mask:url(/assets/icons/${icon}.svg) center/contain no-repeat;` +
            `mask:url(/assets/icons/${icon}.svg) center/contain no-repeat`
        )}
      />
    </Hov>
  )
}

/** One of the two zero-knowledge pairing entry points. */
export function PairingCard({
  card,
  onOpen,
}: {
  card: { title: string; body: string }
  onOpen: () => void
}) {
  return (
    <Hov
      as="button"
      onClick={onOpen}
      style={sx(
        'width:100%;text-align:left;padding:16px 18px;border-radius:var(--radius-lg);' +
          'background:var(--bg-secondary);border:1px solid var(--border-secondary);' +
          'color:var(--text-primary);font-family:inherit;cursor:pointer;' +
          'display:flex;align-items:center;gap:12px'
      )}
      hoverStyle={sx('background:var(--bg-tertiary)')}
    >
      <span style={sx('flex:1;display:flex;flex-direction:column;gap:4px;min-width:0')}>
        <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium);line-height:19px')}>
          {card.title}
        </span>
        <span style={sx('font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)')}>
          {card.body}
        </span>
      </span>
      <span
        style={sx(
          'width:16px;height:16px;flex:none;opacity:.5;background:var(--text-primary);' +
            '-webkit-mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat;' +
            'mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat'
        )}
      />
    </Hov>
  )
}

/**
 * The inline rename, shown in place of whichever device row is being edited.
 *
 * Tapping a device's name opens this: handsets arrive named by whoever set them
 * up, and the person reading the list is the one who has to recognise them.
 */
export function DeviceNameEdit({ v }: { v: Vals }) {
  return (
    <div
      style={sx(
        'padding:14px 16px;border-radius:var(--radius-md);background:var(--bg-secondary);' +
          'border:1px solid var(--border-accent);display:flex;align-items:center;gap:10px'
      )}
    >
      <input
        autoFocus
        value={v.deviceNameDraft}
        onChange={(e) => v.changeDeviceNameDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') v.saveDeviceName()
          if (e.key === 'Escape') v.cancelDeviceName()
        }}
        placeholder="Device name"
        style={sx(
          'flex:1;min-width:0;height:36px;padding:0 12px;border-radius:var(--radius-sm);' +
            'border:1px solid var(--border-input);background:var(--bg-primary);' +
            'color:var(--text-primary);font-family:inherit;font-size:var(--fs-sm);outline:none'
        )}
      />
      <IconButton icon="checkmark" label="Save device name" onClick={v.saveDeviceName} accent />
      <IconButton icon="close" label="Cancel" onClick={v.cancelDeviceName} />
    </div>
  )
}

type Paired = Vals['pairedDevices'][number]

/**
 * A live pairing, with the two ways to end it: mute, or tear it down.
 *
 * Shared with the member's profile — under zero knowledge both sides of a pair
 * hold the same controls, so both render the same row.
 */
export function PairedDeviceRow({ v, d }: { v: Vals; d: Paired }) {
  if (v.deviceNameEditingKey === d.renameKey) return <DeviceNameEdit v={v} />

  return (
    <div
      style={sx(
        'padding:14px 16px;border-radius:var(--radius-md);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);display:flex;align-items:center;gap:12px'
      )}
    >
      <span
        style={sx(
          'width:38px;height:38px;flex:none;border-radius:var(--radius-md);' +
            'background:var(--bg-tertiary);display:flex;align-items:center;justify-content:center'
        )}
      >
        <span
          style={sx(
            `width:19px;height:19px;background:var(--text-primary);` +
              `-webkit-mask:url(${d.icon}) center/contain no-repeat;` +
              `mask:url(${d.icon}) center/contain no-repeat`
          )}
        />
      </span>
      <Hov
        as="button"
        onClick={() => v.startDeviceName(d.renameKey, d.name)}
        title="Rename device"
        style={sx(
          'flex:1;min-width:0;padding:0;border:none;background:transparent;font-family:inherit;' +
            'color:var(--text-primary);text-align:left;cursor:text;display:flex;' +
            'flex-direction:column;gap:2px;border-radius:var(--radius-sm)'
        )}
        hoverStyle={sx('background:var(--bg-tertiary)')}
      >
        <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>{d.name}</span>
        <span style={sx(`font-size:var(--fs-xs);color:${d.statusColor}`)}>{d.statusLabel}</span>
      </Hov>
      <Hov
        as="button"
        onClick={d.togglePause}
        style={sx(
          'height:30px;padding:0 12px;flex:none;border-radius:var(--radius-full);' +
            'border:1px solid var(--border-primary);background:transparent;color:var(--text-primary);' +
            'font-family:inherit;font-size:var(--fs-2xs);font-weight:var(--fw-medium);cursor:pointer'
        )}
        hoverStyle={sx('background:var(--bg-tertiary)')}
      >
        {d.paused ? 'Resume' : 'Pause'}
      </Hov>
      <Hov
        as="button"
        onClick={d.revoke}
        aria-label={`Revoke ${d.name}`}
        title="Revoke pairing"
        style={sx(
          'width:30px;height:30px;flex:none;padding:0;border-radius:var(--radius-full);' +
            'border:1px solid var(--border-critical);background:transparent;cursor:pointer;' +
            'display:flex;align-items:center;justify-content:center'
        )}
        hoverStyle={sx('background:var(--bg-critical-subtle)')}
      >
        <span
          style={sx(
            'width:12px;height:12px;background:var(--text-critical);' +
              '-webkit-mask:url(/assets/icons/close.svg) center/contain no-repeat;' +
              'mask:url(/assets/icons/close.svg) center/contain no-repeat'
          )}
        />
      </Hov>
    </div>
  )
}

/**
 * Group-wide statistics — the one thing here that only exists under the mapped
 * model. Zero knowledge keeps no per-account history, so there is nothing to
 * total, trend or score; saying so on the card is the point of showing it.
 */
function GroupStatsCard({ v }: { v: Vals }) {
  const stats = v.groupStats

  return (
    <div
      style={sx(
        'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:16px'
      )}
    >
      <span style={sx('display:flex;align-items:center;gap:10px')}>
        <span
          style={sx(
            'width:20px;height:20px;flex:none;background:var(--text-primary);' +
              '-webkit-mask:url(/assets/icons/security-score.svg) center/contain no-repeat;' +
              'mask:url(/assets/icons/security-score.svg) center/contain no-repeat'
          )}
        />
        <span style={sx('flex:1;font-size:var(--fs-md);font-weight:var(--fw-medium)')}>
          Group security posture
        </span>
        <span
          style={sx(
            `height:24px;padding:0 10px;flex:none;border-radius:var(--radius-full);` +
              `background:${stats.postureBg};color:${stats.postureColor};` +
              `font-size:var(--fs-2xs);font-weight:var(--fw-medium);display:flex;align-items:center`
          )}
        >
          {stats.posture}
        </span>
      </span>

      <span style={sx('display:flex;gap:18px')}>
        {stats.tiles.map((t) => (
          <span key={t.key} style={sx('display:flex;flex-direction:column;gap:2px;min-width:0')}>
            <span
              style={sx(
                'font-size:var(--fs-3xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm);line-height:30px'
              )}
            >
              {t.value}
            </span>
            <span style={sx('font-size:var(--fs-2xs);line-height:15px;color:var(--text-secondary)')}>
              {t.label}
            </span>
          </span>
        ))}
      </span>

      <span style={sx('display:flex;flex-direction:column;gap:6px')}>
        <span style={sx('display:flex;align-items:flex-end;gap:6px;height:44px')}>
          {stats.week.map((d) => (
            <span
              key={d.key}
              style={sx(
                `flex:1;height:${Math.max(d.heightPct, 8)}%;border-radius:3px;` +
                  `background:${d.today ? 'var(--bg-accent)' : 'var(--bg-tertiary)'}`
              )}
            />
          ))}
        </span>
        <span style={sx('font-size:var(--fs-2xs);color:var(--text-secondary)')}>
          Threats blocked, last 7 days
        </span>
      </span>

      <span
        style={sx(
          'font-size:var(--fs-2xs);line-height:16px;color:var(--text-disabled);' +
            'padding-top:12px;border-top:1px solid var(--border-secondary)'
        )}
      >
        Built from per-account history. Zero knowledge keeps none, so it has nothing to total.
      </span>
    </div>
  )
}
